/**
 * Deep-linkable expense record: /accounting-v2/expenses/:id
 *
 * Exists so an expense can be linked to from a report, a dashboard drill-down or
 * another module, and so a browser refresh lands back on the same record. The
 * list screen's tabs are React state; this page is addressable.
 *
 * Every action here is also permission-gated server-side; the buttons are
 * hidden purely to keep the UI honest about what will succeed.
 */
import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ArrowLeft, FileText, RotateCcw, Send, CheckCircle, XCircle, Banknote } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useExpenseDetail,
  useExpenseAction,
  useRecordRefund,
  type ExpenseStatus,
} from '../hooks/useExpenseApi';

function money(minor: number | undefined, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 })
    .format((Number(minor) || 0) / 100);
}

function longDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: '2-digit' });
}

/** Status → badge tone, so the lifecycle stage reads at a glance. */
const STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'info',
  rejected: 'danger',
  posted: 'primary',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'neutral',
  refunded: 'neutral',
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value}</span>
    </div>
  );
}

export function ExpenseDetailPage() {
  const user = useUser();
  const isEmployee = user?.role === 'employee';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useExpenseDetail(id ?? null);
  const action = useExpenseAction();
  const refund = useRecordRefund();

  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [showRefund, setShowRefund] = useState(false);

  const expense = data?.expense;

  /** Which lifecycle actions are legal for the current status and user role. */
  const available = useMemo(() => {
    if (!expense) return { submit: false, decide: false, post: false, pay: false, cancel: false, refund: false };
    return {
      submit: ['draft', 'rejected'].includes(expense.status),
      decide: !isEmployee && expense.status === 'submitted',
      post: !isEmployee && expense.status === 'approved',
      pay: !isEmployee && ['posted', 'partially_paid'].includes(expense.status) && expense.outstandingMinor > 0,
      cancel: !isEmployee && !['cancelled', 'refunded'].includes(expense.status) && expense.paymentStatus === 'unpaid',
      refund: !isEmployee && !!expense.journalEntryId
        && expense.status !== 'cancelled'
        && expense.refundedAmountMinor < expense.grossAmountMinor,
    };
  }, [expense, isEmployee]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-1">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !expense) {
    return (
      <EmptyState
        icon={<FileText className="h-8 w-8" aria-hidden="true" />}
        title="Expense not found"
        description={getApiErrorMessage(error) || 'This expense may have been deleted, or you may not have access to it.'}
        action={<Button onClick={() => navigate(ROUTES.accountsExpenses)}>Back to expenses</Button>}
      />
    );
  }

  const run = (verb: Parameters<typeof action.mutate>[0]['action'], body?: Record<string, unknown>, done?: string) => {
    action.mutate({ id: expense._id, action: verb, body }, {
      onSuccess: () => {
        toast.success(done || 'Done');
        setShowReject(false);
        setRejectReason('');
      },
      onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Action failed'),
    });
  };

  const outstandingLabel = expense.outstandingMinor > 0
    ? money(expense.outstandingMinor, expense.currency)
    : 'Settled';

  return (
    <div className="space-y-4">
      {/* Breadcrumb + sticky action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to={ROUTES.accountsExpenses}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Expenses
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold text-slate-800">
            {expense.expenseNo}
            <Badge tone={STATUS_TONE[expense.status] ?? 'neutral'}>{expense.status.replace(/_/g, ' ')}</Badge>
            {expense.generatedByRecurrence ? <Badge tone="info">recurring</Badge> : null}
            {expense.isBillable ? <Badge tone="info">billable</Badge> : null}
          </h1>
        </div>

        <div className="flex flex-wrap gap-2">
          {available.submit ? (
            <Button size="sm" onClick={() => run('submit', undefined, 'Submitted for approval')} disabled={action.isPending}>
              <Send className="mr-1 h-4 w-4" aria-hidden="true" /> Submit
            </Button>
          ) : null}
          {available.decide ? (
            <>
              <Button size="sm" onClick={() => run('approve', { level: expense.currentApprovalLevel || 1 }, 'Approved')} disabled={action.isPending}>
                <CheckCircle className="mr-1 h-4 w-4" aria-hidden="true" /> Approve
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowReject((v) => !v)} disabled={action.isPending}>
                <XCircle className="mr-1 h-4 w-4" aria-hidden="true" /> Reject
              </Button>
            </>
          ) : null}
          {available.post ? (
            <Button size="sm" onClick={() => run('post', undefined, 'Posted to the ledger')} disabled={action.isPending}>
              Post to ledger
            </Button>
          ) : null}
          {available.pay ? (
            <Button
              size="sm"
              onClick={() => run('pay', { amountMinor: expense.outstandingMinor }, 'Payment recorded')}
              disabled={action.isPending}
            >
              <Banknote className="mr-1 h-4 w-4" aria-hidden="true" /> Pay {money(expense.outstandingMinor, expense.currency)}
            </Button>
          ) : null}
          {available.refund ? (
            <Button size="sm" variant="secondary" onClick={() => setShowRefund((v) => !v)}>
              <RotateCcw className="mr-1 h-4 w-4" aria-hidden="true" /> Refund
            </Button>
          ) : null}
          {available.cancel ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Reason for cancelling this expense?');
                if (reason && reason.trim()) run('cancel', { reason: reason.trim() }, 'Expense cancelled');
              }}
              disabled={action.isPending}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      </div>

      {showReject ? (
        <Card>
          <CardBody className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Reason for rejection (required)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="max-w-md"
              aria-label="Reason for rejection"
            />
            <Button
              size="sm"
              onClick={() => {
                if (!rejectReason.trim()) { toast.error('A rejection reason is required'); return; }
                run('reject', { level: expense.currentApprovalLevel || 1, reason: rejectReason.trim() }, 'Rejected');
              }}
              disabled={action.isPending}
            >
              Confirm rejection
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowReject(false); setRejectReason(''); }}>
              Dismiss
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {showRefund ? (
        <Card>
          <CardBody className="space-y-2">
            <p className="text-xs text-slate-500">
              Refundable: {money(expense.grossAmountMinor - expense.refundedAmountMinor, expense.currency)}.
              A partial refund reverses input tax proportionally.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="number"
                min={1}
                placeholder="Refund amount in paise"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="max-w-xs"
                aria-label="Refund amount in paise"
              />
              <Button
                size="sm"
                onClick={() => {
                  const amount = Number(refundAmount);
                  if (!Number.isFinite(amount) || amount <= 0) { toast.error('Enter a valid refund amount'); return; }
                  refund.mutate(
                    { expenseId: expense._id, grossAmountMinor: Math.round(amount) },
                    {
                      onSuccess: () => { toast.success('Refund recorded'); setShowRefund(false); setRefundAmount(''); },
                      onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Refund failed'),
                    },
                  );
                }}
                disabled={refund.isPending}
              >
                Record refund
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowRefund(false)}>Dismiss</Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {expense.status === 'rejected' && expense.rejectionReason ? (
        <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <span className="font-medium">Rejected:</span> {expense.rejectionReason}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Financial summary */}
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Amounts</h2>
            <Row label="Net" value={money(expense.netAmountMinor, expense.currency)} />
            {expense.discountMinor > 0 ? <Row label="Discount" value={`− ${money(expense.discountMinor, expense.currency)}`} /> : null}
            <Row label={`Tax (${expense.taxRate}% ${expense.taxTreatment.replace(/_/g, ' ')})`} value={money(expense.taxAmountMinor, expense.currency)} />
            {expense.withholdingTaxMinor > 0 ? <Row label="Withheld" value={`− ${money(expense.withholdingTaxMinor, expense.currency)}`} /> : null}
            <div className="mt-1 border-t border-slate-200 pt-1.5">
              <Row label="Gross" value={money(expense.grossAmountMinor, expense.currency)} />
            </div>
            <Row label="Paid" value={money(expense.paidAmountMinor, expense.currency)} />
            <Row label="Outstanding" value={outstandingLabel} />
            {expense.refundedAmountMinor > 0 ? <Row label="Refunded" value={money(expense.refundedAmountMinor, expense.currency)} /> : null}
            {expense.currency !== 'INR' ? <Row label="Exchange rate" value={expense.exchangeRate} /> : null}
          </CardBody>
        </Card>

        {/* Classification */}
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Details</h2>
            <Row label="Expense date" value={longDate(expense.expenseDate)} />
            <Row label="Posting date" value={longDate(expense.postingDate)} />
            <Row label="Category" value={expense.categoryLabel || '—'} />
            <Row label="Funded by" value={expense.settlementMode.replace(/_/g, ' ')} />
            <Row label="Payment method" value={expense.paymentMethod.replace(/_/g, ' ')} />
            <Row label="Reference" value={expense.referenceNumber || '—'} />
            {expense.supplierName ? <Row label="Supplier" value={expense.supplierName} /> : null}
            {expense.employeeName ? <Row label="Employee" value={expense.employeeName} /> : null}
          </CardBody>
        </Card>

        {/* Dimensions */}
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Allocation</h2>
            <Row label="Department" value={expense.departmentId || '—'} />
            <Row label="Cost centre" value={expense.costCenterId || '—'} />
            <Row label="Project" value={expense.projectId || '—'} />
            <Row label="Campaign" value={expense.campaignId || '—'} />
            <Row label="Billable" value={expense.isBillable ? `Yes (${expense.markupPercent}% markup)` : 'No'} />
            <Row label="Reimbursable" value={expense.isReimbursable ? 'Yes' : 'No'} />
            <Row
              label="Journal"
              value={expense.journalEntryId
                ? <Link to={ROUTES.accountsTransactions} className="text-indigo-600 hover:underline">View ledger</Link>
                : 'Not posted'}
            />
          </CardBody>
        </Card>
      </div>

      {expense.description || expense.notes ? (
        <Card>
          <CardBody>
            <h2 className="mb-1 text-sm font-semibold text-slate-700">Description</h2>
            <p className="text-sm text-slate-600">{expense.description || '—'}</p>
            {expense.notes ? <p className="mt-2 text-xs text-slate-500">{expense.notes}</p> : null}
          </CardBody>
        </Card>
      ) : null}

      {/* Approval timeline */}
      <Card>
        <CardBody>
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Approval history</h2>
          {(data?.approvals ?? []).length === 0 ? (
            <p className="text-sm text-slate-400">No approval activity yet.</p>
          ) : (
            <ol className="space-y-2">
              {(data?.approvals ?? []).map((a) => (
                <li key={a._id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 text-sm last:border-0">
                  <div>
                    <p className="font-medium text-slate-700">
                      Level {a.level} — {a.decision}
                      {a.delegatedFromUserId ? <span className="ml-1 text-xs text-slate-400">(delegated)</span> : null}
                    </p>
                    {a.comments ? <p className="text-xs text-slate-500">{a.comments}</p> : null}
                  </div>
                  <span className="whitespace-nowrap text-xs text-slate-400">
                    {a.approverUserId || a.approverRole || '—'}
                    {a.decidedAt ? ` · ${longDate(a.decidedAt)}` : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>

      {/* Linked transactions */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Payments</h2>
            {(data?.payments ?? []).length === 0 ? (
              <p className="text-sm text-slate-400">No payments recorded.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {(data?.payments ?? []).map((p) => (
                  <li key={p._id} className="flex justify-between border-b border-slate-100 pb-1.5 last:border-0">
                    <span className="text-slate-600">
                      <span className="font-mono text-xs">{p.paymentNo}</span> · {longDate(p.paymentDate)}
                    </span>
                    <span className="font-medium">{money(p.amountMinor, expense.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Refunds</h2>
            {(data?.refunds ?? []).length === 0 ? (
              <p className="text-sm text-slate-400">No refunds recorded.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {(data?.refunds ?? []).map((r) => (
                  <li key={r._id} className="flex justify-between border-b border-slate-100 pb-1.5 last:border-0">
                    <span className="text-slate-600">
                      <span className="font-mono text-xs">{r.refundNo}</span> · {r.isPartial ? 'partial' : 'full'} · {r.reason.replace(/_/g, ' ')}
                    </span>
                    <span className="font-medium">{money(r.grossAmountMinor, expense.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Attachments */}
      {(expense.attachments ?? []).length > 0 ? (
        <Card>
          <CardBody>
            <h2 className="mb-2 text-sm font-semibold text-slate-700">Attachments</h2>
            <ul className="flex flex-wrap gap-2">
              {(expense.attachments ?? []).map((a) => (
                <li key={a._id || a.fileUrl}>
                  <a
                    href={a.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                  >
                    <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    {a.fileName}
                    <span className="text-slate-400">({a.kind})</span>
                  </a>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

export default ExpenseDetailPage;
